import type { setDetailsPanelTabData } from "#skyward_browse";
import { registerErrorDataGatherer } from "#skyward_error";
import { createHiddenInputForm, initialModulesLoaded, isAndroidApp, isIosApp, mobileAccess, submitAndRemoveHiddenInputForm, validateDataParameter, virtualDir } from "#skyward_global";
import { getLayerFromElement, ModalLayer, newModalLayer, showCriticalDialog, showErrorDialog, showInformationBubble, showInformationDialog, CloseReason } from "#skyward_modalLayer";
import { ref } from "vue";
import { type BrowseIdentifier, getPreferences } from "./skyward.ui.browsePreferences";
import { getCallbackStore } from "./skyward.ui.callbackStore";
import { processLoadActions, registerEvents } from "./skyward.ui.events";
import { Dictionary, JQueryInput, TabData } from "./types";

declare global
{
    interface Navigator
    {
        msMaxTouchPoints?: boolean | undefined;
    }
    interface Window
    {
        _androidAjaxHelper?:
        {
            request: (key: string, type: string | undefined, url: string, headers: string, data: string | Dictionary<string, any> | undefined) => void;
            getMetaData: ((key: string) => string) | undefined;
            getResponseBodyAndRemove: ((key: string) => string) | undefined;
        };
        webkit?:
        {
            messageHandlers?:
            {
                ajaxHelper:
                {
                    postMessage: (data: any) => void;
                };
            };
        };
        ReactNativeWebView?:
        {
            postMessage(data: string): void;
        };
    }
    interface MobileDownloadMetadata
    {
        status: number;
        statusText: string;
        headers: string;
    }
}

interface SkywardWindowInternal
{
    // Xamarin helpers. TODO (6543867) Remove these
    _androidAjaxReady: (key: string) => void;
    _androidAjaxFail: (key: string) => void;
    _iosAjaxReady: (key: string, metadata: MobileDownloadMetadata, body: string) => void;
    _iosAjaxFail: (key: string) => void;
    // RN helpers
    _mobileAjaxReady: (key: string, metadata: string, body: string) => void;
    _mobileAjaxFail: (key: string) => void;
}

type UrlParameters = Dictionary<string, string | number | boolean>;

interface NotifyDOMLoadType
{
    skywardContentPanel?: boolean;
}

export interface SubmitFormOptions
{
    openNewWindow: boolean;
    forceNewBrowserWindow: boolean;
    downloadOnMobile: boolean;
    mobileFilename: string | undefined;
    actionVerb: string;
}

type WindowCallback = () => void;

const skyward = window.skyward;
skyward.window = skyward.window || {};

//Private Variables
const win = $(window);
const loadDOMCallbacks = getCallbackStore();
const browseResizeCallbacks = getCallbackStore();
const layoutResizeCallbacks = getCallbackStore();
let domLoadReady = false;
let _isContentLoading = true;

const events =
{
    checkLoadContent: checkLoadContentEvent,
    reload: reloadScreen,
    postForbidden
};

registerEvents("window", events);

registerErrorDataGatherer("window", gatherDataForError);

$(() =>
{
    // Constraints on when we run processLoadActions, which runs viewpart-specific load actions:
    //
    // TriggerOnLoad dependencies (handled via registerLoadDOM) must run before the dataObjectViewer.initChangeTracking
    // viewpart-spcific event or pages with them will always appear dirty.
    //
    // registerLoadDOM callbacks added within viewpart-specific load actions must run during the same call
    // to notifyDOMLoad. This is relied on by informationpopup.js, applicantTracking.js, and responsivetoolbar.js
    registerLoadDOMCallback(processLoadActions);
});

if (mobileAccess)
{
    type MobileDownloadCallback = (body: string, metadata: MobileDownloadMetadata) => void;
    const ajaxRequests: Dictionary<string, MobileDownloadCallback> = {};

    const defaultMetadata = { status: 200, statusText: "OK", headers: "X-Externally-Downloaded: \r\n" };

    const completeWithResult = function (key: string, metadata: MobileDownloadMetadata, body: string)
    {
        const callback = ajaxRequests[key];
        if (callback === undefined)
        {
            return;
        }
        delete ajaxRequests[key];

        callback(body, metadata);
    };

    $.ajaxTransport("maybebinary", function (options, _originalOptions, _jqXHR)
    {
        return {
            key: btoa(crypto.randomUUID()),
            send: function (this: JQuery.Transport & { key: string; }, headers, completeCallback)
            {
                let allHeaders = "";
                $.each(headers, function (k, v)
                {
                    allHeaders += `${k}: ${v}\n`;
                });
                if (headers.Referer === undefined && new URL(options.url!, window.location.href).host === window.location.host)
                {
                    allHeaders += `Referer: ${window.location}\n`;
                }
                if (!("X-Requested-With" in headers))
                {
                    allHeaders += "X-Requested-With: XMLHttpRequest\n";
                }

                ajaxRequests[this.key] = function (body, metadata)
                {
                    completeCallback(metadata.status, metadata.statusText as JQuery.Ajax.TextStatus, { maybebinary: body }, metadata.headers);
                };

                const url = new URL(options.url!, window.location.href).href;
                if (window.ReactNativeWebView)
                {
                    window.ReactNativeWebView!.postMessage(JSON.stringify({
                        key: this.key,
                        type: "qmlativDownloadRequest",
                        method: options.type,
                        url: url,
                        headers: allHeaders,
                        body: options.data
                    }));
                }
                else if (isAndroidApp)
                {
                    window._androidAjaxHelper!.request(this.key, options.type, url, allHeaders, options.data);
                }
                else if (isIosApp)
                {
                    window.webkit!.messageHandlers!.ajaxHelper.postMessage({
                        key: this.key,
                        type: "request",
                        method: options.type,
                        url: url,
                        headers: allHeaders,
                        body: options.data
                    });
                }
            },
            abort: function (this: JQuery.Transport & { key: string; })
            {
                delete ajaxRequests[this.key];
            }
        };
    });

    const ajaxFail = function (key: string)
    {
        completeWithResult(key, { status: 500, statusText: "Internal Server Error", headers: "X-Externally-Downloaded: \r\n" }, "");
    };


    const internalSkywardWindow = skyward.window as unknown as SkywardWindowInternal;
    if (window.ReactNativeWebView)
    {
        internalSkywardWindow._mobileAjaxReady = function (key: string, metadataJson: string, body: string)
        {
            let parsedMetadata: MobileDownloadMetadata | undefined;
            try { parsedMetadata = JSON.parse(metadataJson); } catch { }
            const parsedBody = atob(body ?? "");
            completeWithResult(key, parsedMetadata ?? defaultMetadata, parsedBody);
        };

        internalSkywardWindow._mobileAjaxFail = ajaxFail;
    }
    else if (isAndroidApp && window._androidAjaxHelper)
    {
        internalSkywardWindow._androidAjaxReady = function (key: string)
        {
            const metaDataString = window._androidAjaxHelper?.getMetaData?.(key);
            const metadata = metaDataString ? JSON.parse(metaDataString) as MobileDownloadMetadata : defaultMetadata;
            const body = window._androidAjaxHelper?.getResponseBodyAndRemove?.(key) ?? "";
            completeWithResult(key, metadata, body);
        };

        internalSkywardWindow._androidAjaxFail = ajaxFail;
    }
    else if (isIosApp && window.webkit)
    {
        internalSkywardWindow._iosAjaxReady = function (key: string, metadata: MobileDownloadMetadata, body: string)
        {
            const parsedBody = atob(body ?? "");
            completeWithResult(key, metadata ?? defaultMetadata, parsedBody);
        };

        internalSkywardWindow._iosAjaxFail = ajaxFail;
    }
    else /* emulated mobile or standalone mobile browsers */
    {
        $.ajaxTransport("maybebinary", function (options, _originalOptions, _jqXHR)
        {
            const xhr = options.xhr!();
            xhr.responseType = "blob";

            // TODO (5772911) fix deprecation
            xhr.open(options.type!, options.url!, options.async!);

            const handler: JQuery.Transport =
            {
                send: function (headers, completeCallback)
                {
                    $.each(headers, function (k, v)
                    {
                        xhr.setRequestHeader(String(k), v);
                    });
                    if (!("X-Requested-With" in headers))
                    {
                        xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
                    }

                    const defaultFilename = headers["X-Default-Filename"] || getFilenameFromUrl(options.url!);
                    delete headers["X-Default-Filename"];

                    xhr.onloadend = function ()
                    {
                        // Callers should check for this header and cancel any actions they were planning that anticipated a non-download response.
                        // For example, skyward.window.submitForm closes the popup that it would display a HTML response in.
                        const headers = "x-externally-downloaded: \r\n" + (this.getAllResponseHeaders() || "");
                        completeCallback(this.status, this.statusText as JQuery.Ajax.TextStatus, { maybebinary: "" }, headers);

                        const filename = getFilenameFromHeader(this.getResponseHeader("Content-Disposition")) || defaultFilename;
                        const contentType = this.getResponseHeader("Content-Type") || "application/octet-stream";
                        downloadBlobData(this.response, contentType, filename);
                    };
                    xhr.onerror = function ()
                    {
                        completeCallback(this.status, this.statusText as JQuery.Ajax.TextStatus, { maybebinary: "" }, this.getAllResponseHeaders());
                    };
                    xhr.send(options.data as XMLHttpRequestBodyInit | undefined);
                },
                abort: function ()
                {
                    xhr.abort();
                }
            };
            return handler;

            function getFilenameFromUrl(url: string)
            {
                const lastSlash = url.lastIndexOf("/");
                const start = lastSlash !== -1 ? lastSlash : 0;
                const queryStart = url.indexOf("?", start);
                const end = queryStart !== -1 ? queryStart : undefined;
                const filePart = url.slice(start, end);
                return filePart !== "" ? filePart : "index";
            }
            function getFilenameFromHeader(contentDisposition: string | null)
            {
                if (!contentDisposition)
                {
                    return undefined;
                }
                // https://tools.ietf.org/html/rfc6266#section-4.1
                // This doesn't support filename* because we don't use it
                const filenameMatch = /filename\s*=\s*(.*)/.exec(contentDisposition);
                if (filenameMatch === null)
                {
                    return undefined;
                }
                const filenamePart = filenameMatch[1];
                const search = (filenamePart.startsWith('"')
                    // Double-quotes, followed by zero or more non-quote chars or backslash-escaped chars, followed by an end double-quote
                    ? /"((?:[^\"]|\\.)*)"/
                    // Zero or more non-control or separator chars
                    /* eslint-disable-next-line no-control-regex */
                    : /([^\u0000-\u001f\u007f()<>@,;:\\\/\[\]?={} \"]*)/);
                const result = search.exec(filenamePart);
                return result !== null ? result[1] : undefined;
            }
            function downloadBlobData(response: Blob, contentType: string, filename: string)
            {
                // https://stackoverflow.com/questions/19327749/javascript-blob-filename-without-link/19328891#19328891
                const blob = new Blob([response], { type: contentType });
                const blobUrl = URL.createObjectURL(blob);
                const link = $<HTMLAnchorElement>('<a style="display: none;">');
                if (filename)
                {
                    link[0].download = filename;
                }
                link[0].href = blobUrl;
                $(document.body).append(link);
                link[0].click();
                link.remove();
                URL.revokeObjectURL(blobUrl);
            }
        });
    }
}

let enableResizeEvents = true;
export const toggleResizeEvents = (value: boolean) =>
{
    enableResizeEvents = value;
};

win.on("resize", function ()
{
    if (enableResizeEvents)
    {
        resize();
    }
});


$(async function ()
{
    domLoadReady = true;

    const jqWindow = $(window);

    moduleContent = $("#moduleContent");

    jqWindow.resize();
    await initialModulesLoaded;

    notifyDOMLoad("html", { skywardContentPanel: true });
    if (window.performance && typeof window.performance.mark === "function")
    {
        window.performance.mark("initialNotifyDOMLoadComplete");
    }
    jqWindow.on("beforeunload", function ()
    {
        const warning = notifyDOMUnload();
        if (typeof warning === "string")
        {
            /* set a timeout to hide the loading indicator.  This will execute if they click Stay, but not if they click Leave */
            window.setTimeout(function ()
            {
                $(document).trigger("hideLoadingIndicator");
            }, 1000);

            return warning;
        }
        return undefined;
    });

    if (device)
    {
        $("#master").addClass("device");
    }
});

//This code is to make safari's back button work correctly, and not load a blank page.
window.onunload = function () { };

export interface LoadContentsSettings
{
    enableOpenInNewWindowModifiers: boolean;
    forceNewBrowserWindowOnPopout?: boolean;
    activityIndicator:
    {
        silent: boolean;
        hideGlass: boolean;
        isWorkflowAjaxRequest?: boolean;
    };
    downloadOnMobile: boolean;
    popupRole?: string;
    additionalUrlParams?: UrlParameters;
}

//Public Variables
export let moduleContent: JQuery | null = null;
export const device = /android|applewebkit|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent.toLowerCase());
export const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
export const isSafari = isIosApp || isSafariBrowser;
export const popupCloseFunctionList = [];
export const glassList: JQuery[] = [];
export const popupList: JQuery[] = [];
export function isTouchDevice()
{
    // http://stackoverflow.com/questions/4817029/whats-the-best-way-to-detect-a-touch-screen-device-using-javascript/4819886#4819886
    // as of Modernizr 3.5 touch events don't reliably return true for touch devices
    // TODO (5772911) double check: should we update this?
    return 'ontouchstart' in window ||
        'onmsgesturechange' in window &&
        'msMaxTouchPoints' in window.navigator &&
        window.navigator.msMaxTouchPoints;
}

export function getCurrentPopupId()
{
    return $(document.body).children(".modalLayerContainer").length - 1;
}

export function getNextPopupId()
{
    return $(document.body).children(".modalLayerContainer").length;
}

skyward.window = skyward.window || {};
// Only export getters, otherwise these will be loaded once, for the import statement, and never updated
skyward.window.clientWidth = win.width()!;
skyward.window.clientHeight = win.height()!;
export const getClientWidth = () => skyward.window.clientWidth;
export const getClientHeight = () => skyward.window.clientHeight;
export const clientWidthRef = ref(skyward.window.clientWidth);
export const clientHeightRef = ref(skyward.window.clientHeight);

export let skipUnloadCheck: boolean | undefined;
export function setSkipUnloadCheck(value: boolean | undefined)
{
    skipUnloadCheck = value;
}

export const scrollbarHeight = computeScrollbarSize();

export function hasHorizontalScrollBar(element: JQueryInput)
{
    const domElement = window.jQuery(element);
    if (domElement.length)
    {
        const scrollWidth = domElement[0].scrollWidth;
        const clientWidth = domElement[0].clientWidth;
        if (scrollWidth <= clientWidth)
        {
            return false;
        }
        if (scrollWidth - clientWidth > 1)
        {
            return true;
        }
        // Browsers handle content that overflows by less than a pixel differently; avoid this by looking
        // for the width of the scrollbar itself.  This is mostly relevant to empty browses in IE and Edge,
        // where the "No records" message is computed as being one pixel larger than it should be.
        let oldScrollbarStyle: string | undefined;
        const style = getComputedStyle(domElement[0]);
        const mustSetScrollbar = style.overflowX !== "auto";
        const border = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
        if (mustSetScrollbar)
        {
            oldScrollbarStyle = domElement[0].style.overflowX;
            domElement[0].style.overflowX = "auto";
        }
        const hasScrollbar = Math.round(domElement[0].clientHeight + border) < domElement[0].offsetHeight;
        if (mustSetScrollbar)
        {
            domElement[0].style.overflowX = oldScrollbarStyle!;
        }
        return hasScrollbar;
    }
    return false;
}

export function hasVerticalScrollBar(element: JQueryInput)
{
    const domElement = $(element);
    if (domElement.length)
    {
        return domElement.get(0)!.scrollHeight > domElement.get(0)!.clientHeight;
    }
    return false;
}

export const registerLoadDOMCallback = loadDOMCallbacks.registerCallback;
export const removeLoadDOMCallback = loadDOMCallbacks.deregisterCallback;
export const registerBrowseResizeCallback = browseResizeCallbacks.registerCallback;
export const removeBrowseResizeCallback = browseResizeCallbacks.deregisterCallback;
export const registerLayoutResizeCallback = layoutResizeCallbacks.registerCallback;
export const removeLayoutResizeCallback = layoutResizeCallbacks.deregisterCallback;

export function notifyDOMLoad(selector: JQueryInput, notifyDOMLoadType?: NotifyDOMLoadType): void
{
    if (!domLoadReady)
    {
        try
        {
            console.group("notifyDOMLoad");
            console.warn(`NotifyDOMLoad called on ${selector} before document was ready. This may cause duplicate event handlers or other problems.`);
            console.trace();
            console.groupEnd();
        }
        catch (e) { }
    }
    const domLoadTypes =
    {
        skywardContentPanel: false
    };

    if (notifyDOMLoadType)
    {
        $.extend(domLoadTypes, notifyDOMLoadType);
    }

    const jqSelector = $(selector);
    const context = jqSelector.addIds();

    if (typeof selector !== "string")
    {
        const idSelector: string[] = [];
        jqSelector.each(function ()
        {
            idSelector.push("#" + this.id);
        });

        selector = idSelector.join(",");
    }

    _isContentLoading = true;
    loadDOMCallbacks.dispatchCallbacks.call(context, selector, domLoadTypes);
    _isContentLoading = false;
}

export function isContentLoading()
{
    return _isContentLoading;
}

export function updatePageState(controls: unknown)
{
    return $.ajax(virtualDir + "Security/PageState/Update",
        {
            type: "POST",
            dataType: "JSON",
            activityIndicator: { hideGlass: true },
            data: { controls: JSON.stringify(controls) }
        });
}

export function notifyResize()
{
    if (!domLoadReady)
    {
        try
        {
            console.group("notifyResize");
            console.warn("NotifyResize called before document was ready. This may cause duplicate event handlers or other problems.");
            console.trace();
            console.groupEnd();
        }
        catch (e) { }
    }
    layoutResizeCallbacks.dispatchCallbacks();
    browseResizeCallbacks.dispatchCallbacks();
}

export function closePopup(layer?: ModalLayer, reason?: CloseReason)
{
    // Returns true if layer was closed, false if layer aborted the close, and null if no layer was found to close.
    if (layer && typeof layer.close === 'function')
    {
        return layer.close(reason);
    }

    const topLayer = getLayerFromElement("div.modalLayerContainer:last");
    if (topLayer && typeof topLayer.close === 'function' && topLayer.options.closeOnRecordSelected)
    {
        return topLayer.close(reason);
    }

    return null;
}

export function getImage(imageClass?: string | null)
{
    imageClass ??= "pageLoadingGif";

    const image = document.createElement("img");
    image.src = `${virtualDir}content/loading.gif?v=2`;
    image.className = imageClass;
    return $(image);
}

export function getImageHtml(imageClass?: string | null)
{
    return getImage(imageClass)[0].outerHTML;
}

export function checkLoadContent(target: string, area: string, controller: string, action: string, id?: string | number | null, data?: any, popupId?: string | null, closeFunction?: WindowCallback, successCallback?: WindowCallback | null, resetBreadcrumbs?: boolean, profileObject?: any, errorCallback?: ((layer: ModalLayer, ajax: JQuery.jqXHR) => void) | null, mediaVerificationToken?: string | null, persistModalLayer?: boolean | null, settings?: Partial<LoadContentsSettings>, element?: JQueryInput): JQueryPromise<any>
{
    const deferred = $.Deferred();

    if (element !== undefined && $(element).data().ignoreDirtyDov !== true &&
        //@ts-ignore Cannot import changeTracking due to circular dependency
        skyward.changeTracking.isDirty(getClosestContentPanel(element).find(".dataObjectViewer").first()) || target === "full" && skyward.changeTracking.isDirty())
    {
        //@ts-ignore Cannot import changeTracking due to circular dependency
        skyward.changeTracking.showDirtyScreenModal(function ()
            {
                win.off("beforeunload"); // To prevent the browser's unsaved-changes dialog from showing
                loadContent(target, area, controller, action, id, data, popupId, closeFunction, successCallback, resetBreadcrumbs, profileObject, errorCallback, mediaVerificationToken, null, persistModalLayer, settings, element)
                    .then(deferred.resolve);
            })
            .catch(deferred.reject);
    }
    else
    {
        if (typeof persistModalLayer !== 'boolean' && typeof data === 'object' && !$.isEmptyObject(data))
        {
            persistModalLayer = data.PersistModalLayer;
        }

        if (target !== 'popout' && !persistModalLayer)
        {
            closePopup();
        }

        skyward.detailsPanel.tryCloseDetailsPanel(function ()
        {
            loadContent(target, area, controller, action, id, data, popupId, closeFunction, successCallback, resetBreadcrumbs, profileObject, errorCallback, mediaVerificationToken, null, persistModalLayer, settings, element)
                .then(deferred.resolve);
        });
    }

    return deferred.promise();
}

export const loadContent = function (target: string, area: string, controller: string, action: string, id?: string | number | null, dataParam?: any, popupIdArg?: string | null, closeFunction?: WindowCallback, successCallback?: WindowCallback | null, resetBreadcrumbs?: boolean, profileObject?: any, errorCallback?: ((layer: ModalLayer, ajax: JQuery.jqXHR) => void) | null, mediaVerificationToken?: string | null, preventAjaxResize?: boolean | null, persistModalLayer?: boolean | null, settingsParam?: Partial<LoadContentsSettings>, element?: JQueryInput)
{
    let popupId = popupIdArg;
    let ajax: JQueryPromise<any> & { skyward?: { popupId: string | null | undefined; }; } = $.Deferred().resolve().promise();
    const data = validateDataParameter(dataParam);
    const defaultLoadContentSettings = getDefaultLoadContentSettings();
    const settings = $.extend({}, defaultLoadContentSettings, settingsParam);

    if (typeof persistModalLayer !== 'boolean' && typeof data === 'object' && !$.isEmptyObject(data))
    {
        persistModalLayer = data.PersistModalLayer;
    }

    const clickedElement = element ? $(element) : undefined;
    if (settings.enableOpenInNewWindowModifiers && clickedElement && clickedElement.isLink() && target === "full" && (skyward.keys.globalCtrlPressed || skyward.keys.globalShiftPressed))
    {
        // This is needed to circumvent the default browser behavior that doesn't set the new window to the current tab
        const newSettings = $.extend({}, settings, { enableOpenInNewWindowModifiers: false, forceNewBrowserWindowOnPopout: skyward.keys.globalShiftPressed });
        setTimeout(() => loadContent("popout", area, controller, action, id, dataParam, popupId, closeFunction, successCallback, resetBreadcrumbs, profileObject, errorCallback, mediaVerificationToken, preventAjaxResize, persistModalLayer, newSettings, element), 0);
        return ajax;
    }

    let url = virtualDir;

    if (typeof area === "string" && area.length > 0 && typeof controller === "string" && controller.length > 0)
    {
        url += area + "/" + controller;

        if (typeof action === "string" && action.length > 0)
        {
            url += "/" + action;
        }

        id = AddProfileBackingBrowseSeekId(url, id, data);

        if (typeof id === "number" || typeof id === "string" && id.length > 0)
        {
            url += "/" + id;
        }
    }

    function GetMediaVerificationToken()
    {
        if (mediaVerificationToken)
        {
            return mediaVerificationToken;
        }
        else if (data.mediaVerificationToken)
        {
            return data.mediaVerificationToken;
        }
        return null;
    }

    function AddMediaParameters()
    {
        const mediaHash = GetMediaVerificationToken();
        if (mediaHash)
        {
            urlParams.mediaVerificationToken = mediaHash;

            if (data.preview)
            {
                urlParams.preview = data.preview;
            }

            if (data.filenameOverride)
            {
                urlParams.filenameOverride = data.filenameOverride;
            }

            return true;
        }
        return false;
    }

    /** Look up the browse seek ID for this profile from local storage.
     * @param url The target URL
     * @param id The current target ID
     * @param data Parameter data.  This may be added to.
     * @returns The original ID, if provided, otherwise the browse seek ID if found.
     */
    function AddProfileBackingBrowseSeekId(url: string, id: string | number | undefined | null, data: Dictionary<string, any>)
    {
        // When navigating to a profile screen without an ID provided, such as from the menu, we want to go to the browse seek ID for the profile's
        // backing list.  Initially, we don't know the correspondence between profile screens and browse IDs, so we send no ID, and the server sends
        // us RedirectToSeekID.sxl.  That will tell us what browse ID to use, and we'll cache the URL and browse ID for next time, so we can skip the
        // redirect.  Additionally, some profiles (just Student Profile, at the time of writing) aren't for the same objects as their browses, so we
        // can't use the ID directly.  We cache that fact as well and send the ID as a query parameter in those cases.
        if (target === "full" && (id === null || id === undefined) && $.isEmptyObject(data))
        {
            // `url` should be the full `virtualDir/area/controller/action` path (localStorage is per-domain, so virtualDir is required)
            const profileBrowses = skyward.volatileStorage.getLocalStorage<{ browseId: number, goDirectlyToRecord: boolean; }>("ProfileBrowse");
            const profileData = profileBrowses.get(url);
            if (profileData)
            {
                const browsePrefs = getPreferences(profileData as unknown as BrowseIdentifier);
                // TODO (5772911) double check if parseInt is needed or if seekId is really number | null
                const seekId = browsePrefs && !isNaN(parseInt(browsePrefs.seekId as unknown as string)) ? browsePrefs.seekId : -1;
                if (profileData.goDirectlyToRecord)
                {
                    return seekId;
                }
                else
                {
                    data.browseSeekId = seekId;
                    data.browseId = profileData.browseId;
                }
            }
        }
        return id;
    }

    // query string parameters
    const urlParams: UrlParameters = { ...settingsParam?.additionalUrlParams };
    const notifyDOMLoadType: NotifyDOMLoadType = {};
    if (target === "popup" || target === "select") //Open in a dialog on the current page
    { // No history manipulation.

        const nextPopupId = getNextPopupId();
        const workflowPopupCount = $(".workflowPopup").length;

        for (let i = workflowPopupCount; i < nextPopupId; i++)
        {
            if (!persistModalLayer)
            {
                closePopup();
            }
        }

        popupId = `popup${getNextPopupId()}`;

        const popupContent = $("<div>", { id: popupId, class: "popup" });

        let isWorkflow = false;
        let role;
        if (area === "workflow" || area === "Workflow")
        {
            isWorkflow = true;
            settings.activityIndicator.isWorkflowAjaxRequest = true;
            popupContent.addClass("workflowPopup");
            popupContent.attr("aria-labelledby", `${popupId}_labelWorkflow`);
            popupContent.attr("aria-describedby", `${popupId}_describeWorkflow`);
            role = "dialog";
        }
        else
        {
            popupContent.addClass("window");
            role = settings.popupRole !== undefined && settings.popupRole !== null ? settings.popupRole : clickedElement?.attr("aria-haspopup");
        }

        const layer = newModalLayer(popupContent, closeFunction, { closeOnClick: false, closeOnEscape: false, closeOnRecordSelected: !isWorkflow, role: role });
        // store this for the ajax success callback, in case another dialog pops up in the mean time
        const currentPopupId = getCurrentPopupId();

        urlParams.renderType = "contentOnly";
        urlParams.popupId = currentPopupId;

        if (target === "select")
        {
            urlParams.selectMode = true;
        }

        AddMediaParameters();

        if (!$.isEmptyObject(urlParams))
        {
            url += "?" + $.param(urlParams);
        }

        ajax = $.ajax(
            {
                url: url,
                type: "POST",
                dataType: "viewpartHtml",
                activityIndicator: settings.activityIndicator,
                success: function (data)
                {
                    popupContent.html(data);
                    if (typeof successCallback === 'function')
                    {
                        successCallback();
                    }
                    notifyDOMLoad("#popup" + currentPopupId);
                    popupList.push(popupContent);
                    if (!preventAjaxResize)
                    {
                        resize();
                    }
                },
                statusCode:
                {
                    404: function (jqXhr)
                    {
                        try
                        {
                            if (jqXhr?.responseText !== undefined)
                            {
                                const statusData = JSON.parse(jqXhr.responseText);

                                if (!$.isEmptyObject(statusData) && statusData.message && statusData.synchronousLockWaiting)
                                {
                                    return;
                                }
                            }
                        } catch (e) { }

                        showCriticalDialog("404: Not Found", "Could not locate the URL:\n" + url, null);
                    }
                },
                error: function (jqXhr)
                {
                    if (errorCallback)
                    {
                        errorCallback(layer, jqXhr);
                    }
                    layer.close();
                },
                data: data
            });
    }
    else if (target === "popout") //Open in a new window
    {
        if (data.tab)
        {
            url += "/" + data.tab;
        }

        // Browse automatically starts new history for that window/tab.
        if (popupIdArg)
        {
            const popupNumber = getCurrentPopupId();
            popupId = `popup${popupNumber}`;
            urlParams.popupId = popupNumber;
        }

        const isMedia = AddMediaParameters();
        const submitMode = { openNewWindow: true, forceNewBrowserWindow: settings.forceNewBrowserWindowOnPopout === true, downloadOnMobile: isMedia || settings.downloadOnMobile };

        if (mobileAccess)
        {
            urlParams.blah = (new Date()).getTime();
        }
        url += "?" + $.param(urlParams);
        submitForm(url, data, submitMode);
    }
    else if (target === "full") // Post for a full page load
    {
        if (data.openBrowseFilterDialogFor)
        {
            urlParams.openBrowseFilterDialogFor = data.openBrowseFilterDialogFor;
        }

        if (data.dashboardItemId)
        {
            const dashboardItemLoading = showInformationBubble("Dashboard Item Loading", "Please wait while the Dashboard Item data loads", undefined,
                {
                    closeOnClick: false,
                    closeOnEscape: false
                });
            $.ajax(
                {
                    url: virtualDir + "SkySys/DashboardItem/DashboardItemPreRedirect",
                    type: "POST",
                    dataType: "JSON",
                    activityIndicator: settings.activityIndicator,
                    data:
                    {
                        data: JSON.stringify(data)
                    },
                    success: loadPage,
                    error: function ()
                    {
                        dashboardItemLoading.close("The Dashboard Item could not be loaded");
                        showErrorDialog("Error Loading Dashboard Item", "The Dashboard Item could not be loaded");
                    }
                });
        }
        else
        {
            loadPage();
        }

        // On "full" page load the promise should never resolve
        return $.Deferred().promise();

        function loadPage()
        {
            if (data.tab)
            {
                url += "/" + data.tab;
            }

            if (resetBreadcrumbs)
            {
                data.resetBreadcrumbs = resetBreadcrumbs;
            }

            if (profileObject)
            {
                data.profileObject = profileObject;
            }

            const isMedia = AddMediaParameters();
            const submitMode = { openNewWindow: false, downloadOnMobile: isMedia || settings.downloadOnMobile };

            if (!$.isEmptyObject(urlParams))
            {
                url += "?" + $.param(urlParams);
            }

            if (typeof action === "string" && !(/download|export|render|getfile/i.test(action)))
            {
                $(document.body).trigger('showLoadingIndicator');
            }

            submitForm(url, data, submitMode);
        }
    }
    else if (target === "previous") // Return to the previous screen
    {
        if (action.toLowerCase() === "cancel")
        {
            returnToPrevious(action);
            return ajax;
        }

        $.ajax(
            {
                url: url,
                type: "POST",
                activityIndicator: settings.activityIndicator,
                data: data,
                success: function ()
                {
                    returnToPrevious(action);
                }
            });

        // On "previous" page load the promise should never resolve
        return $.Deferred().promise();
    }
    else if (target === "sortable")
    {
        url += "?renderType=contentOnly";

        const request = $.ajax(
            {
                url: url,
                type: "POST",
                dataType: "viewparthtml",
                activityIndicator: settings.activityIndicator,
                data: data,
            }) as JQuery.jqXHR<string>;
        ajax = $.whenAll([request, import("./skyward.ui.sortable")])
            .done(function ([html, sortable]: [string, typeof import("./skyward.ui.sortable")])
            {
                const parsedHtml = $(html);
                sortable.initialize(parsedHtml as any, area, controller, data);
            });
    }
    else if (target === "reorderdraggable")
    {
        import("./skyward.ui.reorderDraggable").then(function (sortable: typeof import("./skyward.ui.reorderDraggable"))
        {
            sortable.buildReorderDraggablePopUp(url, area, controller, data);
        });
    }
    else //Open in the target element
    {
        if (data.tab)
        {
            url += "/" + data.tab;
            if (target === "detailsPanel" && !profileObject)
            {
                const tabData: TabData =
                {
                    tabType: "details",
                    tab: data.tab
                };

                // @ts-expect-error Go through the skyward object to avoid circular dependency
                (skyward.browse.setDetailsPanelTabData as typeof setDetailsPanelTabData)(tabData);
            }
        }
        urlParams.renderType = "contentOnly";

        const targetContainer = $(target?.length > 0 ?`#${target}` : "");
        if (targetContainer.length === 0)
        {
            console.log(`Could not find window.load() target with ID "${target}"`);
        }
        const modalLayerContainer = targetContainer.closest(".modalLayerContainer");
        if (modalLayerContainer.length > 0)
        {
            const popupNumber = getCurrentPopupId();
            popupId = `popup${popupNumber}`;
            urlParams.popupId = popupNumber;
        }

        // Even though we have it marked not to cached it is still being cached
        // This is a temp work around until there is a solution. (Chrome issue)
        // Does not seem to be an issue with IE or Firefox.
        // http://bugs.jquery.com/ticket/12550
        urlParams.blah = (new Date()).getTime();
        if (target === "detailsPanel")
        {
            urlParams.renderInDetailsPanel = true;
            notifyDOMLoadType.skywardContentPanel = true;
            skyward.detailsPanel.emptyDetailsPanel();
            skyward.detailsPanel.setDetailsPanelData(area, controller, action, id as string, data);
        }

        if (!$.isEmptyObject(urlParams))
        {
            url += "?" + $.param(urlParams);
        }

        // TODO (5772911) fix deprecation
        const xhr = $.ajaxSettings.xhr!();
        ajax = $.ajax(
            {
                url: url,
                type: "POST",
                dataType: "viewpartHtml",
                activityIndicator: settings.activityIndicator,
                xhr: () => xhr,
                success: function (data)
                {
                    if (target === "detailsPanel")
                    {
                        updateDetailsPanelLocation(xhr.responseURL);
                    }
                    targetContainer.trigger("ServerResponsePostLoad");
                    targetContainer.trigger("DOMInsertionPreLoad");
                    targetContainer.html(data);

                    if (typeof successCallback === 'function')
                    {
                        successCallback();
                    }

                    if (typeof target === "string" && target.length > 0)
                    {
                        notifyDOMLoad(`#${target}`, notifyDOMLoadType);
                        targetContainer.trigger("DOMInsertionPostLoad");
                    }

                    if (!preventAjaxResize)
                    {
                        resize();
                    }

                    if (typeof closeFunction === 'function')
                    {
                        closeFunction();
                    }
                    targetContainer.find('.contentArea').addBack('.contentArea').trigger('contentReloaded');
                },
                error: function ()
                {
                    //fail silently
                },
                data: data
            });
        function updateDetailsPanelLocation(location: string)
        {
            if (!location)
            {
                return;
            }
            const url = new URL(location);
            const [, , module, object, screen, id, _tab] = url.pathname.split(/\//g);
            skyward.detailsPanel.setDetailsPanelData(module, object, screen, id, data);
        }
    }
    ajax.skyward = { popupId: popupId };
    return ajax;
};

export function loadContentWithPageLoading(target: string, area: string, controller: string, action: string, id: string | number | null, message: string, data: any, loadingAction: string)
{
    const pageLoadingData =
    {
        area: area,
        controller: controller,
        id: id,
        action: action,
        target: target,
        loadingAction: loadingAction,
        message: message,
        data: JSON.stringify(data)
    };

    loadContent(target, 'Security', 'Window', 'GetPageLoading', null, pageLoadingData);
}

export function updateRelatedRecord(jsondata: any)
{
    $.ajax(virtualDir + "Security/Window/UpdateRelatedRecord",
        {
            type: "POST",
            dataType: "JSON",
            async: false,
            data: jsondata
        });
}

/**
 * Updates the Fiscal Year to the one with the id passed in
 *
 * @param {any} id the id of the Fiscal Year
 */
export function updateFiscalYearSelector(id: string | number)
{
    updateSelector(id, 'FiscalYear');
}

/**
 * Updates the Entity to the one with the id passed in
 *
 * @param id the id of the Entity
 */
export function updateEntitySelector(id: string | number)
{
    updateSelector(id, 'Entity');
}

/**
 * Updates the School Year to the one with the id passed in
 *
 * @param {any} id the id of the School Year
 */
export function updateSchoolYearSelector(id: string | number)
{
    updateSelector(id, 'SchoolYear');
}

export function returnToPrevious(action: string, urlReferrer?: string)
{
    if (action.toLowerCase().includes("dodelete"))
    {
        if (urlReferrer)
        {
            window.location.replace(urlReferrer);
        }
        else
        {
            loadContent('full', 'home', 'home', 'index');
        }
    }
    else if (document.referrer === "")
    {
        // No history. Return home.
        loadContent('full', 'home', 'home', 'index');
    }
    else
    {
        window.location.replace(document.referrer);
    }
}

export function submitForm(url: string, dataParam: Dictionary<string, string>, optionsParam: Partial<SubmitFormOptions>)
{
    const data = validateDataParameter(dataParam);
    const options: SubmitFormOptions = $.extend(
        {
            openNewWindow: false,
            forceNewBrowserWindow: false,
            downloadOnMobile: false,
            mobileFilename: undefined,
            actionVerb: "POST"
        },
        optionsParam);

    const fullUrl = new URL(url, window.location.href);
    const isExternalLink = fullUrl.host !== window.location.host || !fullUrl.pathname.startsWith(virtualDir.slice(0, -1)); // slice off trailing slash

    if (mobileAccess)
    {
        const queryParams = fullUrl.search.split(/[?&]/);
        if (data.mfwOpenAttachment || queryParams.includes("mfwOpenAttachment=true"))
        {
            options.downloadOnMobile = true;
        }
        // Both the iOS and Android apps will open external links in the user's browser, so we want to handle this as a normal form submission.
        // In addition, iOS ignores form submissions with target=_blank.
        if (options.openNewWindow)
        {
            if (data.mfwOpenExternal || queryParams.includes("mfwOpenExternal=true") || isExternalLink)
            {
                options.openNewWindow = false;
            }
        }
    }

    if (mobileAccess && options.downloadOnMobile)
    {
        $.ajax(
            {
                url: url + (url.lastIndexOf("?") === -1 ? "?" : "&") + $.param({ mfwMaybeDownload: true }),
                type: options.actionVerb,
                dataType: "maybebinary",
                data: data,
                headers: { "Accept": "", "X-Default-Filename": options.mobileFilename }
            });
    }
    else if (mobileAccess && options.openNewWindow)
    {
        url += (url.lastIndexOf("?") === -1 ? "?" : "&") + $.param({ renderType: "contentOnly", mfwMaybeDownload: true });

        $.ajax(
            {
                url: url,
                type: options.actionVerb,
                data: data,
                headers: { "Accept": "text/html, text/plain", "X-Default-Filename": options.mobileFilename },
                success: function (data, _status, jqXhr)
                {
                    if (jqXhr.getResponseHeader("X-Externally-Downloaded") !== null)
                    {
                        return;
                    }

                    const content = $("<div>").append(data);

                    content.find(".menuContentArea").attr("class", "menuContentAreaMobile");

                    showInformationDialog("", content.html(), undefined,
                        {
                            allowNoButtons: true,
                            height: "90%",
                            width: "90%",
                            resizable: false
                        });
                }
            });
    }
    else
    {
        const form = createHiddenInputForm(url, data, options.openNewWindow, isExternalLink, options.actionVerb);
        if (options.openNewWindow && options.forceNewBrowserWindow)
        {
            const windowName = "skyward_new_window_" + Math.random().toString(36).substring(2);
            window.open("", windowName,
                "width=" + screen.width + ",height=" + screen.height + ",left=0,top=0,toolbar=yes,location=yes,resizable=yes,scrollbars=yes"
            );
            form.prop("target", windowName);
        }
        submitAndRemoveHiddenInputForm(form);
    }
}

export function checkDirtyScreen(onConfirm: () => void)
{
    const deferred = $.Deferred();

    //@ts-ignore Cannot import changeTracking due to circular dependency
    if (skyward.changeTracking.isDirty())
    {
        //@ts-ignore Cannot import changeTracking due to circular dependency
        skyward.changeTracking.showDirtyScreenModal(onConfirm)
            .then(deferred.resolve)
            .catch(deferred.reject);
    }
    else
    {
        onConfirm();
        deferred.resolve();
    }

    return deferred.promise();
}

export function reloadScreen()
{
    location.reload();
}

export function getClosestContentPanel(element: JQueryInput)
{
    return $(element).closest(".skywardContentPanel");
}

function postForbidden(this: JQueryInput)
{
    const element = $(this);
    const error = element.data("error") as string;

    if (window.parent && window.parent !== window)
    {
        window.parent.postMessage({
            type: "forbidden", data:
            {
                error
            }
        }, "*");
    }
}

function notifyDOMUnload()
{
    let unloadMessage: string | null = null;
    // TODO (5717449)
    // Replace this with a registerNotifyDOMUnload listener so that window.ts doesn't have a dependency on workflow.ts. We don't
    // want to unnecessarily include it in every bundle.
    // @ts-ignore workflow doesn't have a .d.ts and we don't want to import this file
    if (skyward.workflow)
    {
        // @ts-ignore workflow doesn't have a .d.ts and we don't want to import this file
        // This allows us to use this method without including it in the bundle for pages that don't have the workflow module, while still providing enhanced functionality for pages that do
        const pendingWorkflows = skyward.workflow.getPendingWorkflowInstances() ?? [];

        if (pendingWorkflows[0] && pendingWorkflows[0].items && pendingWorkflows[0].items.length)
        {
            $.ajax(`${virtualDir}Security/PageState/Update`,
                {
                    type: "POST",
                    dataType: "JSON",
                    async: false,
                    data: { controls: JSON.stringify(pendingWorkflows) }
                });
        }
    }

    //@ts-ignore Cannot import changeTracking due to circular dependency
    if (skyward.changeTracking?.isDirty() && !skipUnloadCheck)
    {
        unloadMessage = "Unsaved changes on the page. Remain on page and click save to preserve changes.";
    }

    return unloadMessage;
}

function checkLoadContentEvent(this: JQueryInput)
{
    const data = $(this).data();
    checkLoadContent(data.target, data.area, data.controller, data.action, data.id, data.data);
}

function updateSelector(id: string | number, className: string)
{
    const url = `${virtualDir}Security/Window/Update${className}`;
    $.ajax({
        url: url,
        type: "POST",
        data: { newValue: id },
        success: function ()
        {
            window.location.reload();
        }
    });
}

function gatherDataForError()
{
    return {
        currentPopupId: getCurrentPopupId(),
        domLoadReady: domLoadReady,
        windowSize: { width: skyward.window.clientWidth, height: skyward.window.clientHeight }
    };
}

function resize()
{
    // don't use window height: the document may have been resized using CSS to avoid the soft keyboard
    skyward.window.clientWidth = document.documentElement.clientWidth;
    skyward.window.clientHeight = document.documentElement.clientHeight;
    clientWidthRef.value = skyward.window.clientWidth;
    clientHeightRef.value = skyward.window.clientHeight;
    notifyResize();
}

function computeScrollbarSize()
{
    const outer = $('<div style="visibility: hidden; height: 100px;">');
    $(document.body).append(outer);
    const heightNoScroll = outer[0].offsetHeight;
    outer.css({ "overflow": "scroll" });
    const inner = $('<div style="height: 100%">');
    outer.append(inner);
    const heightWithScroll = inner[0].offsetHeight;
    outer.remove();
    return heightNoScroll - heightWithScroll;
}

function getDefaultLoadContentSettings(): LoadContentsSettings
{
    return {
        enableOpenInNewWindowModifiers: true,
        activityIndicator:
        {
            silent: false,
            hideGlass: false
        },
        downloadOnMobile: false
    };
}
